/**
 * Encrypt a secret in the browser to one server's agent (see agent/fieldwork_agent.py open_sealed):
 * ECDH P-256 with a fresh key, HKDF-SHA256 (salt = ephemeral || agent key, info "fieldwork-secret-v1"),
 * AES-256-GCM with "<server id>:<purpose>" as associated data. The panel only relays the result.
 */
export type Sealed = { v: 1; epk: string; iv: string; ct: string };

const enc = new TextEncoder();
const b64 = (b: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(b)));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export async function seal(agentPublicKeyB64: string, serverId: string, purpose: string, secret: string): Promise<Sealed> {
  const agentRaw = unb64(agentPublicKeyB64);
  const agentKey = await crypto.subtle.importKey("raw", agentRaw, { name: "ECDH", namedCurve: "P-256" }, false, []);
  const eph = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, ["deriveBits"]);
  const epkRaw = new Uint8Array(await crypto.subtle.exportKey("raw", eph.publicKey));
  const shared = await crypto.subtle.deriveBits({ name: "ECDH", public: agentKey }, eph.privateKey, 256);
  const hkdf = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  const salt = new Uint8Array(epkRaw.length + agentRaw.length);
  salt.set(epkRaw);
  salt.set(agentRaw, epkRaw.length);
  const key = await crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info: enc.encode("fieldwork-secret-v1") },
    hkdf,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: enc.encode(`${serverId}:${purpose}`) },
    key,
    enc.encode(secret),
  );
  return { v: 1, epk: b64(epkRaw), iv: b64(iv), ct: b64(ct) };
}

/** Short fingerprint of an agent key, shown on the server page so it can be compared with the server. */
export async function fingerprint(agentPublicKeyB64: string): Promise<string> {
  const d = new Uint8Array(await crypto.subtle.digest("SHA-256", unb64(agentPublicKeyB64)));
  return [...d.slice(0, 8)].map((b) => b.toString(16).padStart(2, "0")).join(":");
}

/**
 * The reverse direction (agent/fieldwork_agent.py seal_for_browser): a one-time key pair whose private half
 * never leaves this page, so a secret the server sends back passes through the panel unreadable.
 */
export async function revealKey() {
  const pair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"]);
  const ownRaw = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  return {
    publicKey: b64(ownRaw),
    async open(sealed: Sealed, serverId: string, purpose: string): Promise<string> {
      const epkRaw = unb64(sealed.epk);
      const epk = await crypto.subtle.importKey("raw", epkRaw, { name: "ECDH", namedCurve: "P-256" }, false, []);
      const shared = await crypto.subtle.deriveBits({ name: "ECDH", public: epk }, pair.privateKey, 256);
      const hkdf = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
      const salt = new Uint8Array(epkRaw.length + ownRaw.length);
      salt.set(epkRaw);
      salt.set(ownRaw, epkRaw.length);
      const key = await crypto.subtle.deriveKey(
        { name: "HKDF", hash: "SHA-256", salt, info: enc.encode("fieldwork-reveal-v1") },
        hkdf,
        { name: "AES-GCM", length: 256 },
        false,
        ["decrypt"],
      );
      const pt = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: unb64(sealed.iv), additionalData: enc.encode(`${serverId}:${purpose}`) },
        key,
        unb64(sealed.ct),
      );
      return new TextDecoder().decode(pt);
    },
  };
}
